import {
  Box,
  Container,
  Heading,
  Image,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { LazyImage } from './LazyImage'
import { domain, pageTitle } from '../global'

interface BlogContentProps {
  content: string
}

export interface BlogPostType {
  id: string
  title: string
  content: string
  excerpt: string
  date: string
  image: string
  imageSmall: string
}

const BlogContent: React.FC<BlogContentProps> = ({ content }) => {
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  return (
    <Box
      className="blog-content"
      dangerouslySetInnerHTML={{ __html: content }}
      textAlign="justify"
      sx={{
        h1: {
          color: headingColor,
        },
        h2: {
          fontSize: '2xl',
          fontWeight: 'bold',
          mt: 6,
          mb: 4,
          color: headingColor,
        },
        h3: {
          fontSize: 'xl',
          fontWeight: 'bold',
          mt: 5,
          mb: 3,
          color: headingColor,
        },
        p: { mb: 4 },
        'ul, ol': { mb: 4, pl: 5 },
        li: { mb: 2 },
        a: {
          color: 'purple.500',
          fontWeight: 600,
        },
      }}
    />
  )
}

const BlogPost: React.FC<{ post: BlogPostType }> = ({ post }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const textColor = useColorModeValue('gray.700', 'gray.200')
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const resultBgColor = useColorModeValue('white', 'gray.800')

  const shareUrl = `https://www.snovio.${domain}/blog/${post.id}`
  const title = pageTitle(post.title)
  const description = pageTitle(post.excerpt)
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={post.image} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Box bg={bgColor} color={textColor}>
        <Container maxW="container.xl" py={10}>
          <VStack spacing={6} align="stretch">
            <LazyImage
              src={post.image}
              alt={post.title}
              priority={true}
              height={400}
              width="100%"
              objectFit="cover"
              objectPosition="center"
              borderRadius="md"
            />
            <Heading as="h1" size="2xl" color={headingColor}>
              {post.title}
            </Heading>
          </VStack>
        </Container>
        <Box bg={resultBgColor} py={10}>
          <Container maxW="container.xl">
            <BlogContent content={post.content} />
          </Container>
        </Box>
      </Box>
    </>
  )
}

export default BlogPost
